import React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextField, Box, Autocomplete } from "@mui/material";
import FormDialog from "@components/FormDialog";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "@context/SnackBarProvider";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import useHandleError from "@lib/utils/errorHook";
import { getDrivers } from "@lib/api/queries/drivers";

import {
  createDriverGroup,
  DriverGroupFormData,
  driverGroupSchema,
} from "@lib/api/mutations/driver_groups";
import { DriverGroupResponse } from "@lib/types/driver_group";
import TransferList from "@components/TransferList";
import { Driver, DriversList } from "@lib/types/driver";
import Loading from "@components/Loading";

interface Props {
  addDriverGroupDialogOpen: boolean;
  setAddDriverGroupDialogOpen: (open: boolean) => void;
  refetch?: () => void;
}

const CreateDriverGroup: React.FC<Props> = ({
  addDriverGroupDialogOpen,
  setAddDriverGroupDialogOpen,
  refetch,
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const {
    data: drivers,
    isFetching,
    refetch: refetchDrivers,
    error,
  } = useQuery<Driver[]>({
    queryKey: ["drivers"],
    queryFn: async () => {
      const drivers = await getDrivers();
      return drivers.data;
    },
    enabled: addDriverGroupDialogOpen,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<DriverGroupFormData>({
    resolver: zodResolver(driverGroupSchema),
    defaultValues: {
      name: "",
      description: "",
      drivers: [],
    },
  });

  const { mutate, isPending: isCreatingDriverGroup } = useMutation({
    mutationFn: createDriverGroup,
    onSuccess: (data: DriverGroupResponse) => {
      showSnackbar(t("user.driverGroups.addSuccess"), "success");

      setAddDriverGroupDialogOpen(false);
      reset();
      refetch && refetch();
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);
        errorDetails && showSnackbar(errorDetails.message, "error");
      } else {
        showSnackbar(t("general.error"), "error");
      }
    },
  });

  const handleAddDriver = (data: DriverGroupFormData) => {
    mutate(data);
  };

  return (
    <FormDialog
      title={t("user.driverGroups.add")}
      open={addDriverGroupDialogOpen}
      onClose={() => {
        setAddDriverGroupDialogOpen(false);
        reset();
      }}
      onSubmit={handleSubmit(handleAddDriver)}
      submitText={t("general.save")}
      isSubmitting={isCreatingDriverGroup}
      submittingText={t("general.saving")}
    >
      {isFetching && <Loading />}

      {!isFetching && drivers && (
        <form noValidate>
          <Box display="flex" flexDirection="column" gap={2} py={2}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("user.drivers.name")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("user.drivers.description")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              )}
            />

            <Controller
              name="drivers"
              control={control}
              render={({ field }) => (
                <div>
                  <Autocomplete
                    {...field}
                    multiple
                    options={drivers || []}
                    value={
                      drivers?.filter((driver) =>
                        field.value?.includes(driver.driver_id)
                      ) || []
                    }
                    onChange={(_, value) => {
                      field.onChange(
                        value ? value.map((driver) => driver.driver_id) : []
                      );
                    }}
                    loading={isFetching}
                    isOptionEqualToValue={(option, value) =>
                      option.driver_id === value.driver_id
                    }
                    getOptionLabel={(option) => option.name || ""}
                    renderOption={(props, option) => (
                      <li {...props} key={`${option.name}-${option.driver_id}`}>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("user.bindings.driver")}
                        placeholder={t("user.bindings.selectDriver")}
                        variant="outlined"
                        error={!!errors.drivers}
                        helperText={errors.drivers?.message}
                      />
                    )}
                  />
                </div>
              )}
            />
          </Box>
        </form>
      )}
    </FormDialog>
  );
};

export default CreateDriverGroup;
